import { VarNum } from './';
import { InputMode } from '@/model/VarNum';

export class Scenario {
    public id: number;
    public title: string = 'Scenario';
    public adjustResultForInflation: boolean = true;

    public price: number = 321800;
    public rent: number = 0;
    public condoFees: number = 0;
    public propertyTax: number = 3400;
    public utilities: number = 250;
    public maintenance: number = 2000;
    public insurance: number = 60;
    public outfitting: number = 1000;

    public buyCostFlat: number = 300 + 500 + 880 + 280 - 1800;  //appraisal, inspection, lawyer, title insurance, first time homebuyer's tax credit
    public buyCostPct: number = 0.3;  //land transfer
    public sellCostFlat: number = 1500;
    public sellCostPct: number = 5;

    public downPaymentPct: number = 5;
    public mortgageInterestRatePct: VarNum = new VarNum(1.64);
    public mortgageLength: number = 25;
    public mortgageTerm: number = 5;
    public fthbSharePct: number = 0;
    public sellAtYear: number = 6;
    public investmentRatePct: VarNum = new VarNum(8);
    public houseAppreciationRatePct: VarNum = new VarNum(2);
    public costGrowthRatePct: number = 2.0;

    constructor(id: number) {
        this.id = id;

        this.mortgageInterestRatePct.mode = InputMode.StepRamp;
        this.mortgageInterestRatePct.v1 = 3.0;
        this.mortgageInterestRatePct.rampStart = 5;
        this.mortgageInterestRatePct.rampLength = 10;

        this.houseAppreciationRatePct.mode = InputMode.StepRamp;
        this.houseAppreciationRatePct.rampStart = 1;
        this.houseAppreciationRatePct.v1 = 2.5;
    }

    public calculate(): Result {
      const s = this;

      // Initial
      let cmhcRate = 0;
      if (s.downPaymentPct < 10) {
        cmhcRate = 0.04;
      } else if (s.downPaymentPct < 15) {
        cmhcRate = 0.031;
      } else if (s.downPaymentPct < 20) {
        cmhcRate = 0.028;
      }
      let propValue = s.price;  //value appreciates beyond initial closing price
      const fthbShare = s.price * (s.fthbSharePct / 100);
      const down = s.price * (s.downPaymentPct / 100);
      const cmhcCost = s.price * cmhcRate;
      let mortgage = s.price + cmhcCost - down - fthbShare;
      let principalBuilt = down - cmhcCost + fthbShare;

      let total = down + s.price * (s.buyCostPct / 100) + s.buyCostFlat + s.outfitting;
      let totalYearly = 0;
      let totalAnnuity = 0;
      const initialTotal = total;
      let stockTotal = initialTotal;

      // Year-by-year
      let curRent = s.rent;
      let curPropTax = s.propertyTax / 12;
      let curCondoFees = s.condoFees;
      let curMaint = s.maintenance / 12;
      let curUtilities = s.utilities;
      let curInsurance = s.insurance;
      let curMortgageMonthlyPayment = 0.0;
      for (let year = 0; year < s.sellAtYear; year++) {
        const effectiveRates = s.effectiveMortgageInterestRt(year);
        if (year % s.mortgageTerm === 0) {
          curMortgageMonthlyPayment = s.monthlyMortgagePayment(mortgage, effectiveRates.monthly, (s.mortgageLength - year) * 12);
        }

        // Simulate monthly
        const monthlyPropValueMult = s.annualToMonthlyRateMult(s.houseAppreciationRatePct.Value(year) / 100);
        const monthlyStockGrowthMult = s.annualToMonthlyRateMult(s.investmentRatePct.Value(year) / 100);
        const monthlyInflationMult = s.annualToMonthlyRateMult(s.costGrowthRatePct / 100);
        for (let month = 0; month < 12; month++) {
          const monthTotal = curMortgageMonthlyPayment + curRent + curPropTax + curCondoFees + curUtilities + curInsurance + curMaint;
          total += monthTotal;
          totalYearly += monthTotal;
          totalAnnuity += curMortgageMonthlyPayment + curRent;

          const interest = mortgage * effectiveRates.monthly;
          const princip = curMortgageMonthlyPayment - interest;

          principalBuilt += princip;
          mortgage -= princip;
          propValue *= monthlyPropValueMult;

          stockTotal += monthTotal;
          stockTotal *= monthlyStockGrowthMult;

          curRent *= monthlyInflationMult;
          curPropTax *= monthlyInflationMult;
          curCondoFees *= monthlyInflationMult;
          curMaint *= monthlyInflationMult;
          curUtilities *= monthlyInflationMult;
          curInsurance *= monthlyInflationMult;
        }
      }

      // Final
      const inflationFactor = (this.adjustResultForInflation ? Math.pow(1 + s.costGrowthRatePct / 100, this.sellAtYear) : 1.0);
      const sellCost = propValue * (s.sellCostPct / 100) + (s.sellCostFlat * inflationFactor);
      total += sellCost;
      stockTotal -= initialTotal + totalYearly;  //we care about the growth only, not contributions (we already accounted for those)
      principalBuilt += propValue - s.price;  //house appreciation
      total += stockTotal;
      total += fthbShare;
      total -= principalBuilt;

      return {
          totalCost: total / inflationFactor,
          avgMonthly: totalYearly / inflationFactor / s.sellAtYear / 12,
          principalBuilt: principalBuilt / inflationFactor,
          opportunityCost: stockTotal / inflationFactor,
          initialAndFinalCosts: (initialTotal + sellCost) / inflationFactor,
          totalOngoing: totalYearly / inflationFactor,
          totalAnnuityAndRent: totalAnnuity / inflationFactor,
      };
    }

    private annualToMonthlyRateMult(annual: number) {
      return 1 + Math.pow(1 + annual, 1 / 12.0) - 1;
    }

    private monthlyMortgagePayment(principal: number, monthlyRt: number, monthsRemain: number) {
      // ref: spreadsheet in http://www.yorku.ca/amarshal/mortgage.htm
      return (principal * monthlyRt) / (1 - Math.pow(1 + monthlyRt, -monthsRemain));
    }

    private effectiveMortgageInterestRt(year: number) {
      const norminalAnnualRt = this.mortgageInterestRatePct.Value(year) / 100;
      const effectiveMonthlyRt = Math.pow(Math.pow(1 + norminalAnnualRt / 2, 2), 1 / 12.0) - 1;
      return { annual: Math.pow(1 + effectiveMonthlyRt, 12) - 1, monthly: effectiveMonthlyRt };
    }
}

export interface Result {
    totalCost: number;
    avgMonthly: number;
    principalBuilt: number;
    opportunityCost: number;
    initialAndFinalCosts: number;
    totalOngoing: number;
    totalAnnuityAndRent: number;
}
